/**
 * Wire protocol between the Wordloom client and server (Socket.IO).
 *
 * - Every client→server event carries an acknowledgement callback that
 *   receives an `Ack<T>`.
 * - The server pushes `room:state` (a per-seat `RoomView`) after EVERY change.
 *   `RoomView.version` is monotonic per room; clients drop older versions.
 * - Every inbound payload is validated server-side with the zod schemas in
 *   ./schemas (a separate entry point, '@wordloom/engine/schemas').
 *   Game-mutating intents carry `turn` (must equal the game's `turnNumber`, so
 *   a stale intent can never apply to a newer position) and `actionId`
 *   (idempotency key: a retried intent returns the original ack).
 */
import type {
  ActionPayload,
  ActivityPayload,
  CreateRoomPayload,
  DictCheckPayload,
  ExchangePayload,
  JoinRoomPayload,
  PeekRoomPayload,
  PlayPayload,
  RematchPayload,
  TurnActionPayload,
} from './schemas';
import type { Coord, GameSettings, GameView, MoveErrorCode, Seat } from './types';

export const PROTOCOL_VERSION = 1;

/** Room codes: unambiguous upper-case letters and digits (no 0/O, 1/I). */
export const ROOM_CODE_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
export const ROOM_CODE_LENGTH = 5;
export const NAME_MAX_LENGTH = 20;
/** After the opponent has been disconnected this long, `game:claimWin` is accepted. */
export const DISCONNECT_CLAIM_AFTER_MS = 60_000;
/** Max words per `dict:check` request. */
export const DICT_CHECK_MAX_WORDS = 24;

// ---------------------------------------------------------------------------
// Errors & acks
// ---------------------------------------------------------------------------

export type ErrorCode =
  | MoveErrorCode
  | 'BAD_REQUEST'
  | 'RATE_LIMITED'
  | 'ROOM_NOT_FOUND'
  | 'ROOM_FULL'
  | 'NOT_IN_ROOM'
  | 'GAME_NOT_ACTIVE'
  | 'STALE_TURN'
  | 'CLAIM_NOT_AVAILABLE'
  | 'REMATCH_NOT_AVAILABLE'
  | 'INTERNAL';

export interface ErrorPayload {
  code: ErrorCode;
  /** Friendly message, ready to display. */
  message: string;
  invalidWords?: string[];
  cells?: Coord[];
}

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export type Ack<T extends object = {}> = ({ ok: true } & T) | { ok: false; error: ErrorPayload };

// ---------------------------------------------------------------------------
// Room view (server → client)
// ---------------------------------------------------------------------------

export type RoomStatus = 'waiting' | 'playing' | 'finished';

/** Live "what is the player doing" signal, for the opponent's thinking indicator. */
export type Activity = 'idle' | 'arranging';

export interface SeatView {
  seat: Seat;
  name: string;
  connected: boolean;
  /** Epoch ms of the disconnect, or null while connected. */
  disconnectedAt: number | null;
  /** Games won in this room (rematch series). */
  wins: number;
  activity: Activity;
}

/** Everything one seat may see about a room. Sent as `room:state`. */
export interface RoomView {
  code: string;
  /** Monotonic per room. Clients must ignore a view older than the one they have. */
  version: number;
  status: RoomStatus;
  /** The viewer's seat. */
  you: Seat;
  hostSeat: Seat;
  seats: [SeatView | null, SeatView | null];
  settings: GameSettings;
  /** Current (or just-finished) game; null while waiting for the first opponent. */
  game: GameView | null;
  /** 1-based number of the current game in this room (0 before the first starts). */
  gameNumber: number;
  /** Seats that have asked for a rematch (after a game finished). */
  rematchVotes: Seat[];
  /**
   * While the opponent is disconnected during a game: epoch ms after which the
   * viewer may send `game:claimWin`. Null otherwise.
   */
  claimWinAvailableAt: number | null;
  /** Server clock when this view was produced (clients derive a clock offset). */
  serverNow: number;
}

// ---------------------------------------------------------------------------
// Client → server payloads
// ---------------------------------------------------------------------------

// The payload types are inferred from the zod schemas in ./schemas. Only the
// TYPES come through here: the engine root must never import zod at runtime,
// or every client bundle (which imports the root) would ship it. The server
// imports the schemas themselves from '@wordloom/engine/schemas'.
export type {
  ActionPayload,
  ActivityPayload,
  CreateRoomPayload,
  DictCheckPayload,
  ExchangePayload,
  JoinRoomPayload,
  PeekRoomPayload,
  PlayPayload,
  RematchPayload,
  TurnActionPayload,
} from './schemas';

// ---------------------------------------------------------------------------
// Ack result shapes
// ---------------------------------------------------------------------------

export interface JoinResult {
  code: string;
  /** Secret seat token. Store it (per room) to reclaim the seat on reconnect. */
  token: string;
  seat: Seat;
}

/**
 * `room:peek`: what the invite is for, shown on the join form before a name is
 * typed. Only a room with a free seat answers (else ROOM_NOT_FOUND / ROOM_FULL,
 * exactly as `room:join` would), so it tells a stranger nothing a join would not.
 */
export interface RoomPeek {
  /** The host's name, or null if the host's seat is free. */
  hostName: string | null;
  settings: GameSettings;
}

export interface PlayResult {
  score: number;
  words: { word: string; score: number }[];
  bingo: boolean;
}

export interface DictCheckResult {
  /** Keyed by the UPPER-CASE word. */
  results: Record<string, boolean>;
}

// ---------------------------------------------------------------------------
// Typed Socket.IO event maps
// ---------------------------------------------------------------------------

export interface ClientToServerEvents {
  'room:create': (p: CreateRoomPayload, ack: (r: Ack<JoinResult>) => void) => void;
  'room:join': (p: JoinRoomPayload, ack: (r: Ack<JoinResult>) => void) => void;
  /** Read-only: the host and settings of a room with a free seat (the invite join form). */
  'room:peek': (p: PeekRoomPayload, ack: (r: Ack<RoomPeek>) => void) => void;
  /** Leave the room. While waiting this frees the seat; mid-game it is a disconnect (not a resignation). */
  'room:leave': (ack: (r: Ack) => void) => void;
  'game:play': (p: PlayPayload, ack: (r: Ack<PlayResult>) => void) => void;
  'game:exchange': (p: ExchangePayload, ack: (r: Ack) => void) => void;
  'game:pass': (p: TurnActionPayload, ack: (r: Ack) => void) => void;
  'game:resign': (p: ActionPayload, ack: (r: Ack) => void) => void;
  /** Win by forfeit once the opponent has been disconnected ≥ DISCONNECT_CLAIM_AFTER_MS. */
  'game:claimWin': (p: ActionPayload, ack: (r: Ack) => void) => void;
  /** Vote for (or withdraw from) a rematch after a finished game. Both votes → new game. */
  'game:rematch': (p: RematchPayload, ack: (r: Ack) => void) => void;
  /** Throttled on the client; only transitions are sent. Relayed via SeatView.activity. */
  'game:activity': (p: ActivityPayload) => void;
  /** Dictionary lookups for the pre-submit preview. Rate-limited. */
  'dict:check': (p: DictCheckPayload, ack: (r: Ack<DictCheckResult>) => void) => void;
}

export interface ServerHello {
  /** Git short SHA the server was built from (compare with the client's __BUILD_SHA__). */
  buildSha: string;
  buildTime: string;
  protocolVersion: number;
  serverNow: number;
  /** Name of the active dictionary, e.g. "word-list (274k)". */
  dictionary: string;
}

export interface KickedPayload {
  reason: 'replaced' | 'room-closed';
  message: string;
}

export interface ServerToClientEvents {
  /** Sent once on every (re)connection, before anything else. */
  'server:hello': (p: ServerHello) => void;
  'room:state': (view: RoomView) => void;
  /** This socket no longer holds its seat (e.g. the same player opened another tab). */
  'room:kicked': (p: KickedPayload) => void;
}
